import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { propertyService } from 'api/services/propertyService';
import { availabilityService } from 'api/services/availabilityService';
import type { InventoryCalendarResponse, CategoryCalendar, InventoryDay } from 'api/types/availability.types';
import BookingFormModal from 'components/modals/modules/booking-module/BookingFormModal';
// Shared with the Booking Worksheet's free-inventory strip — one scale, so a
// night cannot read 'healthy' on one screen and 'critical' on the other.
import { getAvailColor, getAvailBg } from './availability.colors';
import type { StayData } from 'components/modals/modules/booking-module/BookingFormModal';

// ============================================================================
// CONSTANTS
// ============================================================================

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_ABBRS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================================
// HELPERS
// ============================================================================

const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

// ============================================================================
// COMPONENT
// ============================================================================

interface AvailabilityScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const AvailabilityScreen: React.FC<AvailabilityScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  // --- State ---
  const now = new Date();
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedPropertyLabel, setSelectedPropertyLabel] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based
  const [calendarData, setCalendarData] = useState<InventoryCalendarResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{ categoryId: string; date: string } | null>(null);
  const hoveredTdRef = useRef<HTMLTableCellElement | null>(null);

  // --- Click-to-book state ---
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [prefilledStayData, setPrefilledStayData] = useState<Partial<StayData> | undefined>(undefined);

  // --- Property dropdown ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const handlePropertySelect = useCallback((val: string | string[] | null, _: any, option?: SelectOption | null) => {
    setSelectedProperty((val as string) || '');
    setSelectedPropertyLabel(option?.label || '');
  }, []);

  // --- Click-to-book: double-click a calendar cell to open booking form ---
  const handleCellDoubleClick = useCallback((dateStr: string) => {
    if (!selectedProperty) return;
    if (!allowedActions.includes('add')) return;
    const parts = selectedPropertyLabel.split(' - ');
    const checkOut = new Date(dateStr);
    checkOut.setDate(checkOut.getDate() + 1);
    const checkOutStr = checkOut.toISOString().split('T')[0];
    setPrefilledStayData({
      propertyId: selectedProperty,
      propertyCode: parts[0]?.trim() || '',
      propertyName: parts.slice(1).join(' - ')?.trim() || '',
      checkInDate: dateStr,
      checkOutDate: checkOutStr,
    });
    setBookingFormOpen(true);
  }, [selectedProperty, selectedPropertyLabel, allowedActions]);

  // --- Fetch calendar data ---
  const fetchCalendar = useCallback(async () => {
    if (!selectedProperty) {
      setCalendarData(null);
      return;
    }
    setLoading(true);
    try {
      const res = await availabilityService.getCalendar(selectedProperty, year, month);
      if (res.success && res.data) {
        setCalendarData(res.data);
      } else {
        setCalendarData(null);
      }
    } catch {
      setCalendarData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, year, month]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // --- Month navigation ---
  const handlePrevMonth = useCallback(() => {
    if (month === 1) {
      setMonth(12);
      setYear(prev => prev - 1);
    } else {
      setMonth(prev => prev - 1);
    }
  }, [month]);

  const handleNextMonth = useCallback(() => {
    if (month === 12) {
      setMonth(1);
      setYear(prev => prev + 1);
    } else {
      setMonth(prev => prev + 1);
    }
  }, [month]);

  const handleToday = useCallback(() => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  }, []);

  // --- Build date columns ---
  const daysCount = getDaysInMonth(year, month);
  const dates = useMemo(() => {
    return Array.from({ length: daysCount }, (_, i) => {
      const d = new Date(year, month - 1, i + 1);
      return {
        day: i + 1,
        dayOfWeek: d.getDay(),
        dateStr: `${year}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isToday: d.toDateString() === new Date().toDateString(),
      };
    });
  }, [year, month, daysCount]);

  // --- Build lookup for quick cell data ---
  const cellLookup = useMemo(() => {
    const map = new Map<string, InventoryDay>();
    if (!calendarData?.categories) return map;
    for (const cat of calendarData.categories) {
      for (const day of cat.days) {
        map.set(`${cat.roomCategoryId}-${day.date.split('T')[0]}`, day);
      }
    }
    return map;
  }, [calendarData]);

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: fetchCalendar, tooltip: 'Refresh Calendar', variant: 'icon' },
  ], [fetchCalendar]);

  const categories: CategoryCalendar[] = calendarData?.categories ?? [];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Room Availability"
        description="View room inventory and availability calendar"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Controls Bar */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem',
          flexWrap: 'wrap', flexShrink: 0,
        }}>
          {/* Property selector */}
          <div style={{ minWidth: '280px' }}>
            <SyutSelect
              label="Property"
              isAsync
              loadOptions={loadPropertyOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={selectedProperty}
              onChange={handlePropertySelect as any}
              placeholder="Select property..."
              size="md"
            />
          </div>

          {/* Month navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="btn-icon" onClick={handlePrevMonth} title="Previous month">
              <IconSystem.chevronLeft size={18} />
            </button>
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', minWidth: '140px', textAlign: 'center' }}>
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <button className="btn-icon" onClick={handleNextMonth} title="Next month">
              <IconSystem.chevronRight size={18} />
            </button>
            <button className="btn btn-secondary" onClick={handleToday} style={{ fontSize: '0.75rem', marginLeft: '0.25rem' }}>
              Today
            </button>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto', alignItems: 'center', fontSize: '0.7rem' }}>
            <LegendItem color="var(--success, #16a34a)" label="> 50%" />
            <LegendItem color="var(--warning, #d97706)" label="20-50%" />
            <LegendItem color="var(--danger, #dc2626)" label="1-20%" />
            <LegendItem color="var(--text-muted)" label="0%" />
          </div>
        </div>

        {/* Calendar Grid */}
        {!selectedProperty ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Select a property to view the availability calendar.
          </div>
        ) : loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Loading calendar data...
          </div>
        ) : categories.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            No room categories or inventory data found for this property.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.5rem' }}>
            <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
              <thead>
                <tr>
                  {/* Category header */}
                  <th style={{
                    position: 'sticky', left: 0, zIndex: 2,
                    padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)',
                    borderBottom: '2px solid var(--border-color)', borderRight: '2px solid var(--border-color)',
                    fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left',
                    minWidth: '160px', whiteSpace: 'nowrap',
                  }}>
                    Room Category
                  </th>
                  {/* Date headers */}
                  {dates.map(d => (
                    <th key={d.day} style={{
                      padding: '0.25rem 0.125rem', background: d.isToday ? 'var(--primary)' : d.isWeekend ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                      borderBottom: '2px solid var(--border-color)', borderRight: '1px solid var(--border-color)',
                      fontSize: '0.65rem', fontWeight: 500, textAlign: 'center', minWidth: '44px',
                      color: d.isToday ? 'white' : 'var(--text-muted)',
                    }}>
                      <div>{DAY_ABBRS[d.dayOfWeek]}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>{d.day}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.roomCategoryId}>
                    {/* Category name (sticky) */}
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 1,
                      padding: '0.5rem 0.75rem', background: 'var(--bg-primary)',
                      borderBottom: '1px solid var(--border-color)', borderRight: '2px solid var(--border-color)',
                      fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap',
                    }}>
                      <div style={{ color: 'var(--text-primary)' }}>{cat.categoryName}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{cat.categoryCode} · {cat.totalRooms} rooms</div>
                    </td>
                    {/* Day cells */}
                    {dates.map(d => {
                      const cell = cellLookup.get(`${cat.roomCategoryId}-${d.dateStr}`);
                      const available = cell?.availableRooms ?? cat.totalRooms;
                      const total = cell?.totalRooms ?? cat.totalRooms;
                      const oversold = cell?.oversoldRooms ?? 0;
                      const isHovered = hoveredCell?.categoryId === cat.roomCategoryId && hoveredCell?.date === d.dateStr;

                      return (
                        <td
                          key={d.day}
                          ref={isHovered ? hoveredTdRef : undefined}
                          onMouseEnter={() => setHoveredCell({ categoryId: cat.roomCategoryId, date: d.dateStr })}
                          onMouseLeave={() => setHoveredCell(null)}
                          onDoubleClick={() => handleCellDoubleClick(d.dateStr)}
                          style={{
                            padding: '0.375rem 0.25rem', textAlign: 'center',
                            borderBottom: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)',
                            background: isHovered ? 'var(--bg-secondary)' : getAvailBg(available, total, oversold),
                            cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          title={oversold > 0
                            ? `Oversold by ${oversold} room${oversold === 1 ? '' : 's'} — committed demand exceeds inventory on this night`
                            : undefined}
                        >
                          {oversold > 0 ? (
                            /* The excess itself is the number worth reading here.
                               "0/10" says the night sold out; "−2" says the property
                               owes two rooms it does not have. */
                            <span style={{
                              fontSize: '0.75rem', fontWeight: 700,
                              color: getAvailColor(available, total, oversold),
                            }}>
                              −{oversold}
                            </span>
                          ) : (
                            <span style={{
                              fontSize: '0.75rem', fontWeight: 600,
                              color: getAvailColor(available, total),
                            }}>
                              {available}
                            </span>
                          )}
                          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>/{total}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Viewport-clamped hover card (portal-rendered to escape the table's overflow:auto parent) */}
      {hoveredCell && hoveredTdRef.current && (() => {
        const cell = cellLookup.get(`${hoveredCell.categoryId}-${hoveredCell.date}`);
        const cat = categories.find(c => c.roomCategoryId === hoveredCell.categoryId);
        if (!cell || !cat) return null;
        return (
          <HoverCard
            anchorRect={hoveredTdRef.current.getBoundingClientRect()}
            cell={cell}
            categoryName={cat.categoryName}
            date={hoveredCell.date}
          />
        );
      })()}

      {/* Click-to-book modal */}
      <BookingFormModal
        isOpen={bookingFormOpen}
        mode="create"
        onSave={() => { setBookingFormOpen(false); fetchCalendar(); }}
        onClose={() => setBookingFormOpen(false)}
        initialStayData={prefilledStayData}
        // Without this the modal falls back to [] and an availability refusal
        // can never offer the oversell path, however the booking was started.
        allowedActions={allowedActions}
      />
    </div>
  );
};

// ============================================================================
// LEGEND ITEM
// ============================================================================

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
    <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: color }} />
    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
  </div>
);

// ============================================================================
// HOVER CARD — portal-rendered, viewport-clamped tooltip for calendar cells
// ============================================================================

interface HoverCardProps {
  anchorRect: DOMRect;
  cell: InventoryDay;
  categoryName: string;
  date: string;
}

const HoverCard: React.FC<HoverCardProps> = ({ anchorRect, cell, categoryName, date }) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Tentative position above the cell; hidden until phase-2 measurement runs to avoid a one-frame flash.
  const [pos, setPos] = useState<{ top: number; left: number; visible: boolean }>({
    top: anchorRect.top,
    left: anchorRect.left + anchorRect.width / 2,
    visible: false,
  });

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 8;
    const MARGIN = 8;

    // Vertical: above by default; flip below if it would overflow the top edge
    let top = anchorRect.top - rect.height - GAP;
    if (top < MARGIN) {
      top = anchorRect.bottom + GAP;
      if (top + rect.height > vh - MARGIN) top = Math.max(MARGIN, vh - rect.height - MARGIN);
    }

    // Horizontal: centered on the cell, clamped to viewport
    let left = anchorRect.left + anchorRect.width / 2 - rect.width / 2;
    if (left < MARGIN) left = MARGIN;
    if (left + rect.width > vw - MARGIN) left = vw - rect.width - MARGIN;

    setPos({ top, left, visible: true });
  }, [anchorRect.top, anchorRect.left, anchorRect.width, anchorRect.height]);

  return createPortal(
    <div
      ref={cardRef}
      style={{
        position: 'fixed', top: pos.top, left: pos.left,
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem',
        padding: '0.5rem 0.75rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10000,
        whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'var(--text-primary)',
        pointerEvents: 'none',
        visibility: pos.visible ? 'visible' : 'hidden',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{categoryName} — {date}</div>
      <div>Total: {cell.totalRooms}</div>
      <div>Booked: {cell.confirmedRooms}</div>
      <div>Pending: {cell.pendingApprovalRooms}</div>
      {cell.holdRooms > 0 && <div>Hold: {cell.holdRooms}</div>}
      {cell.inHouseRooms > 0 && <div>In-House: {cell.inHouseRooms}</div>}
      <div>Blocked: {cell.blockedRooms}</div>
      <div>OOO: {cell.oooRooms}</div>
      <div style={{ fontWeight: 600, color: getAvailColor(cell.availableRooms, cell.totalRooms, cell.oversoldRooms) }}>
        Available: {cell.availableRooms}
      </div>
      {cell.oversoldRooms > 0 && (
        <div style={{ fontWeight: 600, color: 'var(--danger, #dc2626)' }}>
          Oversold: {cell.oversoldRooms}
        </div>
      )}
    </div>,
    document.body
  );
};

export default AvailabilityScreen;
